/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and international
 * treaties. Unauthorized use, reproduction, distribution, or modification of any content
 * from Knowledge for Decisions (K4D) is strictly prohibited. For inquiries regarding
 * the use or licensing of K4D's opyrighted materials, please contact us at
 * contact@k4d.com.br. "Knowledge for Decisions (K4D)" and the slogan "Using data to make
 * precise decisions" are registered trademarks of Knowledge for Decisions (K4D).
 */

/** What can be done to one row, from the row itself. */

import React, { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Text, UnstyledButton } from '@/shared/ui';
import { ROLE_ORDER } from '@/features/agency-settings-v2/constants/role-colors';
import type { TeamSeatBlock } from '@/services/requests/housien-services/AgenciesService.team';
import { moveTargets } from './team-rows';
import { offersInviteActions } from './invite-actions';

const CheckIcon: React.FC = () => (
  <svg
    width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"
  >
    <path d="M20 6 9 17l-5-5" />
  </svg>
);

const SectionLabel: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Text
    component="span"
    style={{
      padding: 'var(--space-1-5) var(--space-2-5) var(--space-1)',
      fontSize: 'var(--text-tag)', fontWeight: 'var(--font-bold)',
      letterSpacing: 'var(--tracking-chip)', textTransform: 'uppercase',
      color: 'var(--text-tertiary-soft)',
    }}
  >
    {children}
  </Text>
);

const Item: React.FC<{
  onClick: () => void;
  disabled?: boolean;
  danger?: boolean;
  checked?: boolean;
  children: React.ReactNode;
}> = ({ onClick, disabled, danger, checked, children }) => (
  <UnstyledButton
    onClick={onClick}
    disabled={disabled}
    style={{
      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      gap: 'var(--space-3)', height: 34, padding: '0 var(--space-2-5)',
      borderRadius: 'var(--radius-md)', background: 'none', textAlign: 'left',
      fontSize: 'var(--text-small)', width: '100%',
      cursor: disabled ? 'not-allowed' : 'pointer',
      opacity: disabled ? 0.45 : 1,
      color: danger ? 'var(--status-error-text)' : 'var(--text-primary)',
    }}
  >
    <Text component="span">{children}</Text>
    {checked && <CheckIcon />}
  </UnstyledButton>
);

export interface RowMenuProps {
  /** Null on a pending row: an invitation has no seat to move yet. */
  currentPlanId: string | null;
  /** Whether the reader may mint a fresh invitation token. */
  canInvite?: boolean;
  /** Sends the invitation again. Only reached on a pending row. */
  onResend?: () => void;
  /** Puts the invitation's link on the clipboard. */
  onCopyLink?: () => void;
  currentRole: string | null;
  blocks: TeamSeatBlock[];
  planTitle: (planId: string) => string | null;
  pending: boolean;
  onRole: (role: string) => void;
  onMove: (planId: string) => void;
  onRemove: () => void;
  onClose: () => void;
}

/**
 * The row's own menu.
 *
 * Moving somebody is offered beside changing their role because they are the
 * same kind of decision from the reader's side — what this person is here to
 * do — even though one changes permissions and the other changes what they
 * are allowed to spend.
 */
export const RowMenu: React.FC<RowMenuProps> = ({
  currentPlanId, currentRole, blocks, planTitle, pending, canInvite = false,
  onRole, onMove, onRemove, onResend, onCopyLink, onClose,
}) => {
  const { t } = useTranslation('features/agency-settings');
  const box = useRef<HTMLDivElement>(null);

  // A menu that stays open behind the next click is a menu the reader has to
  // dismiss before they can do anything else.
  useEffect(() => {
    const away = (event: MouseEvent) => {
      if (!box.current?.contains(event.target as Node)) onClose();
    };
    const escape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('mousedown', away);
    document.addEventListener('keydown', escape);
    return () => {
      document.removeEventListener('mousedown', away);
      document.removeEventListener('keydown', escape);
    };
  }, [onClose]);

  const targets = moveTargets(blocks, currentPlanId);

  return (
    <div
      ref={box}
      role="menu"
      style={{
        position: 'absolute', right: 'var(--space-5)', top: 'calc(100% - 6px)',
        zIndex: 20, width: 240, display: 'flex', flexDirection: 'column',
        padding: 'var(--space-1-5)', border: '1px solid var(--border-subtle)',
        borderRadius: 'var(--radius-lg)', background: 'var(--surface-raised)',
        boxShadow: 'var(--shadow-lg)',
      }}
    >
      <SectionLabel>{t('v2.team.menuRole')}</SectionLabel>
      {ROLE_ORDER.map((role) => (
        <Item
          key={role}
          onClick={() => onRole(role)}
          checked={role === currentRole}
        >
          {t(`v2.roles.${role}`)}
        </Item>
      ))}

      {/* An invitation has no seat yet: it is holding one, and where its
          holder will sit is settled when they accept. */}
      {!pending && targets.length > 1 && (
        <>
          <SectionLabel>{t('v2.team.menuMove')}</SectionLabel>
          {targets.map((target) => (
            <Item
              key={target.planId}
              onClick={() => onMove(target.planId)}
              disabled={target.disabled}
              checked={target.current}
            >
              {planTitle(target.planId) ?? t('v2.team.planUnknown')}
            </Item>
          ))}
        </>
      )}

      {/* Only on an invitation, and only to somebody who could have sent
          it: both mint a fresh token, so these are a working way into the
          agency rather than a convenience. */}
      {offersInviteActions(pending, canInvite) && (
        <>
          <Item onClick={() => { onResend?.(); onClose(); }}>
            {t('v2.team.menuResend')}
          </Item>
          <Item onClick={() => { onCopyLink?.(); onClose(); }}>
            {t('v2.team.menuCopyLink')}
          </Item>
        </>
      )}

      <Item onClick={onRemove} danger>
        {pending ? t('v2.team.menuCancelInvite') : t('v2.team.menuRemove')}
      </Item>
    </div>
  );
};
